"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { forumService, timeAgo, forumPath } from "../_modules/components/ForumService";
import { useForumAccess } from "../_modules/hooks/useForumAccess";

// Moderation queue for platform admins: pending reports with one-click
// outcomes, the rails' recent blocks and flags, and the floor's numbers.
const REASON_LABELS: Record<string, string> = {
  spam: "Spam or repetitive content",
  harassment: "Harassment or disrespect",
  pii: "Client PII / compliance risk",
  misinformation: "Misleading product or carrier claims",
  self_promotion: "Excessive self-promotion / recruiting",
  other: "Something else",
};
type Tab = "pending" | "actioned" | "dismissed";

export default function ForumModPage() {
  const { isModerator, isLoading } = useForumAccess();
  const [tab, setTab] = useState<Tab>("pending");
  const [busy, setBusy] = useState<string | null>(null);
  const [slugs, setSlugs] = useState<Record<string, string>>({});
  const qc = useQueryClient();
  const { toast } = useToast();

  const stats = useQuery({ queryKey: ["forum", "mod", "stats"], queryFn: forumService.mod.getStats, enabled: isModerator, staleTime: 30_000 });
  const reports = useQuery({ queryKey: ["forum", "mod", "reports", tab], queryFn: () => forumService.mod.getReports(tab), enabled: isModerator, staleTime: 15_000 });
  const screenings = useQuery({ queryKey: ["forum", "mod", "screenings"], queryFn: () => forumService.mod.getScreenings(undefined, 30), enabled: isModerator, staleTime: 60_000 });

  // Comment reports only carry the post id; resolve the slug for the link.
  useEffect(() => {
    const missing = [...new Set((reports.data || []).filter((r: any) => r.targetType === "comment" && r.target?.postId && !slugs[r.target.postId]).map((r: any) => r.target.postId as string))];
    if (!missing.length) return;
    let cancelled = false;
    Promise.all(missing.map(async (id) => [id, (await forumService.getPost(id).catch(() => null))?.slug] as const)).then((pairs) => {
      if (cancelled) return;
      const found: Record<string, string> = {};
      for (const [id, slug] of pairs) if (slug) found[id] = slug;
      if (Object.keys(found).length) setSlugs((s) => ({ ...s, ...found }));
    });
    return () => {
      cancelled = true;
    };
  }, [reports.data, slugs]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["forum", "mod"] });
    qc.invalidateQueries({ queryKey: ["forum", "posts"] });
  };

  const act = async (r: any, outcome: "remove" | "dismiss" | "handled") => {
    setBusy(r.id);
    try {
      if (outcome === "remove") {
        const reason = `Report: ${REASON_LABELS[r.reason] || r.reason}`;
        if (r.targetType === "post") await forumService.mod.removePost(r.targetId, reason);
        else await forumService.mod.removeComment(r.targetId, reason);
        await forumService.mod.reviewReport(r.id, "actioned");
        toast({ title: "Content removed", description: "The report is marked actioned.", duration: 2500 });
      } else {
        await forumService.mod.reviewReport(r.id, outcome === "dismiss" ? "dismissed" : "actioned");
        toast({ title: outcome === "dismiss" ? "Report dismissed" : "Marked handled", duration: 2000 });
      }
      refresh();
    } catch (e: any) {
      toast({ title: "Action failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  if (isLoading) return <p className="font-mono text-[12px] text-muted-foreground">Checking your access…</p>;
  if (!isModerator) {
    return (
      <Card className="max-w-lg">
        <CardContent className="p-6">
          <p className="forum-serif text-[20px] text-foreground">This page is for the InsuraCentral moderation team.</p>
          <Link href={forumPath()} className="mt-3 inline-block text-[13px] font-semibold text-primary hover:underline">
            Back to the Community
          </Link>
        </CardContent>
      </Card>
    );
  }

  const s = stats.data;
  const linkFor = (r: any) => {
    const slug = r.targetType === "post" ? r.target?.slug : r.target?.postId ? slugs[r.target.postId] : null;
    return slug ? forumPath(`/t/${slug}`) : null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">InsuraCentral team</p>
          <h1 className="forum-serif flex items-center gap-2 text-[24px] font-medium text-foreground">
            <ShieldCheck className="h-5 w-5 text-primary" /> Moderation queue
          </h1>
        </div>
        <Link href={forumPath()} className="text-[13px] font-medium text-muted-foreground hover:text-primary">
          Back to the floor
        </Link>
      </div>

      {s && (
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Pending reports", s.pending_reports],
            ["Blocked by rails, 24h", s.blocked_24h],
            ["Flagged by rails, 7d", s.flagged_7d],
            ["Banned accounts", s.banned_users],
            ["Threads", s.posts],
            ["Replies", s.comments],
            ["Posters this week", s.weekly_posters],
          ].map(([label, n]) => (
            <div key={String(label)} className="rounded-lg border bg-card px-3.5 py-3">
              <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</dt>
              <dd className="mt-1 font-mono text-[20px] font-semibold tabular-nums text-foreground">{n as number}</dd>
            </div>
          ))}
        </dl>
      )}

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="forum-serif text-[20px] font-medium text-foreground">Reports</h2>
          <div className="inline-flex rounded-md border bg-card p-0.5">
            {(["pending", "actioned", "dismissed"] as Tab[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setTab(v)}
                className={`inline-flex h-8 items-center rounded px-3 text-[12px] font-semibold capitalize transition-colors ${tab === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
        {reports.isLoading ? (
          <p className="mt-4 font-mono text-[12px] text-muted-foreground">Loading…</p>
        ) : !reports.data?.length ? (
          <p className="mt-4 rounded-lg border bg-card px-5 py-8 text-center text-[14px] text-muted-foreground">Nothing {tab} right now.</p>
        ) : (
          <ol className="mt-4 divide-y rounded-lg border bg-card">
            {reports.data.map((r: any) => {
              const href = linkFor(r);
              const removed = r.target?.moderationStatus && r.target.moderationStatus !== "visible";
              const text = r.targetType === "post" ? r.target?.title : r.target?.body || "(reply)";
              return (
                <li key={r.id} className="px-4 py-3.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                    <span className="font-mono uppercase tracking-[0.12em] text-muted-foreground">{r.targetType === "post" ? "Thread" : "Reply"}</span>
                    <span className="rounded-sm border border-destructive/30 bg-destructive/10 px-1.5 py-px font-semibold text-destructive">{REASON_LABELS[r.reason] || r.reason}</span>
                    {r.reportCount > 1 && <span className="font-mono text-muted-foreground">× {r.reportCount} reports</span>}
                    {removed && <span className="font-mono text-muted-foreground">already {r.target.moderationStatus}</span>}
                    <span className="ml-auto font-mono tabular-nums text-muted-foreground">{timeAgo(r.createdAt)}</span>
                  </div>
                  <p className="forum-serif mt-1.5 text-[16px] leading-snug text-foreground">
                    {href ? <Link href={href} className="hover:text-primary hover:underline">{text}</Link> : text}
                  </p>
                  {r.details && <p className="mt-1 text-[13px] text-muted-foreground">“{r.details}”</p>}
                  <p className="mt-1 font-mono text-[11px] text-muted-foreground">reported by {r.reporter?.name}</p>
                  {tab === "pending" && (
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      {!removed && (
                        <Button size="sm" variant="destructive" className="h-8" disabled={busy === r.id} onClick={() => act(r, "remove")}>
                          Remove content
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8" disabled={busy === r.id} onClick={() => act(r, "handled")}>
                        Mark handled
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8" disabled={busy === r.id} onClick={() => act(r, "dismiss")}>
                        Dismiss
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <section>
        <h2 className="forum-serif text-[20px] font-medium text-foreground">Rails, last 30 decisions</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">What the automatic screening blocked or flagged. Blocks never reached the floor.</p>
        {!screenings.data?.length ? (
          <p className="mt-4 rounded-lg border bg-card px-5 py-8 text-center text-[14px] text-muted-foreground">No automated decisions yet.</p>
        ) : (
          <ol className="mt-4 divide-y rounded-lg border bg-card">
            {screenings.data.map((x: any) => (
              <li key={x.id} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                  <span className={`rounded-sm border px-1.5 py-px font-semibold ${x.decision === "block" ? "border-destructive/30 bg-destructive/10 text-destructive" : "border-amber-300 bg-amber-50 text-amber-800"}`}>{x.decision}</span>
                  <span className="font-mono uppercase tracking-[0.12em] text-muted-foreground">{x.kind} · {x.layer}{x.category ? ` · ${x.category}` : ""}</span>
                  <span className="text-muted-foreground">{x.userName || x.user_name}</span>
                  <span className="ml-auto font-mono tabular-nums text-muted-foreground">{timeAgo(x.createdAt || x.created_at)}</span>
                </div>
                {x.reason && <p className="mt-1 text-[13px] text-foreground/90">{x.reason}</p>}
                {x.excerpt && <p className="mt-0.5 truncate text-[12px] text-muted-foreground">{x.excerpt}</p>}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
